import { FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { MedicalImageInterface } from '@/shared/config';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { Checkbox } from '@/shared/ui';

import { PathologyGroup } from '@/entities/assets';
import { getRootTypeMessage, periapicalMessages } from '@/entities/condition';
import { reportsModel } from '@/entities/reports';

import { useFDA3DGPRestrictions } from '@/features/fdaRestrictions';

import { GroupMedicalImagesList } from '../GroupMedicalImagesList/GroupMedicalImagesList';

import styles from './PeriapicalRadiolucencySection.module.scss';

type PeriapicalRadiolucencySectionProps = {
  group: PathologyGroup;
  selectedMedicalImages?: MedicalImageInterface[];
  toggleSelected?: (medicalImage: MedicalImageInterface) => void;
};

export const PeriapicalRadiolucencySection: FC<
  PeriapicalRadiolucencySectionProps
> = ({ group, selectedMedicalImages, toggleSelected }) => {
  const [hoveredGuide, setHoveredGuide] = useState('');
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();

  const showPeriapicalRadiolucencyMasks = useAppSelector(
    reportsModel.selectors.selectShowPeriapicalRadiolucencyMasks,
  );

  const { shouldHidePeriapicalVolume } = useFDA3DGPRestrictions();

  const handleShowPeriapicalRadiolucencyMasks = () => {
    dispatch(
      reportsModel.actions.setShowPeriapicalRadiolucencyMasks(
        !showPeriapicalRadiolucencyMasks,
      ),
    );
  };

  if (!group.rootGroups || group.rootGroups.length === 0) {
    return null;
  }

  const sortedRootGroups = [...group.rootGroups].sort(
    (a, b) => a.rootType - b.rootType,
  );

  return (
    <div
      className={styles.container}
      data-component="periapical-radiolucency-section"
    >
      <div className={styles.header}>
        <h4 className="h4">
          {formatMessage(periapicalMessages.periapicalRadiolucency)}
        </h4>
        <Checkbox
          checked={showPeriapicalRadiolucencyMasks}
          onChange={handleShowPeriapicalRadiolucencyMasks}
        >
          <FormattedMessage id="global.showMasks" defaultMessage="Show Masks" />
        </Checkbox>
      </div>

      <div className={styles.rootGroups}>
        {sortedRootGroups.map((rootGroup) => {
          const rootMessage = getRootTypeMessage(rootGroup.rootType);
          const rootName = formatMessage(rootMessage);
          const shouldShowRootName = rootName !== '-';

          const rootAssetIDs = [
            ...new Set(rootGroup.assets.map((asset) => asset.ID)),
          ];
          const rootMedicalImages =
            group.medicalImages?.filter((image) =>
              rootAssetIDs.includes(image.assetID),
            ) || [];

          if (rootMedicalImages.length === 0) {
            return null;
          }

          return (
            <div
              key={`root_${rootGroup.rootType}`}
              className={styles.rootGroup}
            >
              {rootGroup.volume > 0 && !shouldHidePeriapicalVolume && (
                <div className={styles.rootHeader}>
                  <p className="p3">
                    <FormattedMessage
                      {...periapicalMessages.periapicalVolume}
                      values={{ volume: rootGroup.volume.toFixed(3) }}
                    />
                    {shouldShowRootName && <> ({rootName})</>}
                  </p>
                </div>
              )}
              <GroupMedicalImagesList
                medicalImages={rootMedicalImages}
                focusedImageID={hoveredGuide}
                selectedMedicalImages={selectedMedicalImages}
                toggleSelected={toggleSelected}
                onHoveredGuideChanged={setHoveredGuide}
                hideShowMasksCheckbox
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};
